import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, Boxes, Grid3X3, MapPin, Archive, Settings, Package } from "lucide-react";

/**
 * MobileNav - Burger menu navigation for mobile devices
 * Only renders on screens < 1200px width
 */
export default function MobileNav() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1200);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    // Using callback pattern to avoid lint warning about setState in effect
    const closeDrawer = () => {
      if (isOpen) setIsOpen(false);
    };
    closeDrawer();
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close drawer on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = "hidden";
    }
    
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Don't render on desktop
  if (!isMobile) return null;

  const navItems = [
    { to: "/block-builder", label: "Block Builder", icon: Boxes },
    { to: "/ctm-slicer", label: "Connected Textures", icon: Grid3X3 },
    { to: "/topo-import", label: "World Builder", icon: MapPin },
    { to: "/asset-tools", label: "Asset Tools", icon: Archive },
    { to: "/resource-pack", label: "Resource Pack", icon: Package },
    { to: "/admin", label: "Admin", icon: Settings },
  ];

  return (
    <>
      {/* Burger Button */}
      <button
        className="mobile-nav-toggle mobile-only"
        onClick={() => setIsOpen(true)}
        aria-label="Open navigation menu"
        data-testid="mobile-nav-toggle"
      >
        <Menu size={24} />
      </button>

      {/* Overlay + Drawer */}
      <div 
        className={`mobile-nav-overlay ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen(false)}
        aria-hidden={!isOpen}
      >
        <nav 
          className="mobile-nav-drawer"
          onClick={(e) => e.stopPropagation()}
          role="navigation"
          aria-label="Main navigation"
        >
          {/* Background image */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
            backgroundImage: "url(/mobile-menu-bg.webp)",
            backgroundSize: "cover", backgroundPosition: "center",
            opacity: 0.05, zIndex: 0, pointerEvents: "none"
          }} />
          {/* Header */}
          <div className="mobile-nav-header" style={{ position: "relative", zIndex: 1 }}>
            <span className="mobile-nav-title">Navigation</span>
            <button
              className="mobile-nav-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close navigation menu"
            >
              <X size={20} />
            </button>
          </div>

          {/* Nav Links */}
          <div className="mobile-nav-links" style={{ position: "relative", zIndex: 1 }}>
            {navItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) => 
                  `mobile-nav-link ${isActive ? "active" : ""}`
                }
                data-testid={`mobile-nav-${to.replace("/", "") || "home"}`}
              >
                <Icon size={20} />
                {label}
              </NavLink>
            ))}
          </div>

          {/* Footer with version */}
          <div style={{
            padding: "16px",
            borderTop: "1px solid rgba(255, 255, 255, 0.08)",
            fontSize: "11px",
            color: "#666",
            textAlign: "center",
            position: "relative",
            zIndex: 1
          }}>
            LumenForge • Prototype
          </div>
        </nav>
      </div>
    </>
  );
}
